import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Label,
  MultiSelect,
  TrafficBadge,
  Spinner,
  Alert,
  AlertTitle,
  AlertDescription,
  CompactButton,
} from "@ogt/ui";
import type { LineDetails, Stop } from "../../../../types";
import {
  fetchParentStopsViaFind,
  fetchTrafficMetaViaFind,
} from "../../../../api/lineParentStops.fromFind";
import { useTrafficMeta } from "../../../../utils/useTrafficStyle";
import { selectionSummary } from "../../../../utils/selectionSummary";
import { getLinePublicLabel } from "../../../../utils/lineDisplay";

const ALL_STOPS_VALUE = "__all_stops__";

const SV_COLLATOR = new Intl.Collator("sv", {
  sensitivity: "base",
  numeric: true,
});

const normalizeParentId = (raw: string | number) => {
  const s = String(raw).trim();
  return s.split("-")[0];
};

function ensureParentFields<
  T extends Pick<Stop, "id" | "name" | "lat" | "lng">
>(s: T & Partial<Pick<Stop, "parentLat" | "parentLng">>): Stop {
  const parentLat = s.parentLat ?? s.lat;
  const parentLng = s.parentLng ?? s.lng;
  const id = normalizeParentId(s.id);

  return { ...(s as any), id, parentLat, parentLng } as Stop;
}

function uniqueStrings(values: Array<unknown>) {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (value == null) continue;

    const str = String(value).trim();
    if (!str) continue;
    if (seen.has(str)) continue;

    seen.add(str);
    result.push(str);
  }

  return result;
}

function getTrafficMetaLookupIds(line: LineDetails) {
  const anyLine = line as any;
  const publicLabel = getLinePublicLabel(line);

  return uniqueStrings([
    anyLine.id,
    anyLine.LineNrReal,
    anyLine.LineName,
    anyLine.PublicLineNumber,
    anyLine.LineNo,
    anyLine.LineNumber,
    anyLine.Designation,
    anyLine.Name,
    anyLine.name,
    publicLabel,
  ]);
}

export interface LineCardProps {
  line: LineDetails;
  active: boolean;
  onActivate: (line: LineDetails) => void;
  onRemove: (lineId: string) => void;
  liveUnavailable?: boolean;
  showLiveChecked: boolean;
  onToggleShowLive: (lineId: string, checked: boolean) => void;

  onAddStopFromLine: (s: Stop) => void;

  allStops?: Stop[];
  selectedStopsGlobal?: Stop[];
  onRemoveStopGlobal?: (stopId: string) => void;
}

export function LineCard({
  line,
  showLiveChecked,
  onToggleShowLive,
  onAddStopFromLine,
  allStops,
  liveUnavailable,
  selectedStopsGlobal = [],
  onRemoveStopGlobal,
}: LineCardProps) {
  const lineKey = String(line.id);

  const liveIsActive = showLiveChecked && !liveUnavailable;

  const parentFromStops = React.useMemo<Stop[]>(() => {
    const inStops = line.stops ?? [];

    if (!inStops.length) return [];

    const map = new Map<string, Stop>();

    for (const sp of inStops) {
      const parentId = normalizeParentId((sp as any).parentId ?? sp.id);

      if (!map.has(parentId)) {
        const lat = (sp as any).parentLat ?? sp.lat ?? 0;
        const lng = (sp as any).parentLng ?? sp.lng ?? 0;
        const baseName = (sp.name ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim();

        map.set(
          parentId,
          ensureParentFields({
            id: parentId,
            name: baseName,
            lat,
            lng,
            parentLat: lat,
            parentLng: lng,
          })
        );
      }
    }

    return Array.from(map.values());
  }, [line.stops]);

  const parentFromAllStops = React.useMemo<Stop[] | null>(() => {
    if (!allStops?.length) return null;

    const serving = allStops.filter((s) =>
      (s as any).servedLineIds?.includes(lineKey)
    );

    if (!serving.length) return [];

    const byParent = new Map<string, Stop>();

    for (const s of serving) {
      const parentId = normalizeParentId(s.id);

      if (!byParent.has(parentId)) {
        const name = (s.name ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim();

        byParent.set(
          parentId,
          ensureParentFields({
            id: parentId,
            name,
            lat: s.lat,
            lng: s.lng,
            parentLat: s.lat,
            parentLng: s.lng,
          })
        );
      }
    }

    return Array.from(byParent.values());
  }, [allStops, lineKey]);

  const [parentStops, setParentStops] = React.useState<Stop[]>(parentFromStops);
  const [loading, setLoading] = React.useState(false);

  const lineNo = (line as any).LineName ?? line.id;
  const municipality = (line as any).municipalityName;

  const lineLabel = municipality
    ? `Linje ${lineNo}, ${municipality}`
    : `Linje ${lineNo}`;

  React.useEffect(() => {
    let alive = true;

    if (parentFromStops.length > 0) {
      setParentStops((prev) =>
        shallowEqualStops(prev, parentFromStops) ? prev : parentFromStops
      );
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const viaFind = await fetchParentStopsViaFind(lineKey);
        const viaFindNorm = viaFind.map((s) => ensureParentFields(s));

        if (!alive) return;

        if (viaFindNorm.length > 0) {
          setParentStops((prev) =>
            shallowEqualStops(prev, viaFindNorm) ? prev : viaFindNorm
          );
          return;
        }

        if (parentFromAllStops && parentFromAllStops.length > 0) {
          setParentStops((prev) =>
            shallowEqualStops(prev, parentFromAllStops)
              ? prev
              : parentFromAllStops
          );
        } else {
          setParentStops((prev) => (prev.length === 0 ? prev : []));
        }
      } catch {
        if (!alive) return;

        if (parentFromAllStops && parentFromAllStops.length > 0) {
          setParentStops((prev) =>
            shallowEqualStops(prev, parentFromAllStops)
              ? prev
              : parentFromAllStops
          );
        } else {
          setParentStops((prev) => (prev.length === 0 ? prev : []));
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [lineKey, parentFromStops, parentFromAllStops]);

  const byId = React.useMemo(() => {
    const map = new Map<string, Stop>();

    for (const stop of parentStops) {
      map.set(String(stop.id), stop);
    }

    return map;
  }, [parentStops]);

  const parentStopIds = React.useMemo(
    () => parentStops.map((s) => String(s.id)),
    [parentStops]
  );

  const optionIds = React.useMemo(
    () => new Set(parentStopIds),
    [parentStopIds]
  );

  const cleanStopIds = React.useCallback(
    (ids: string[]) =>
      Array.from(
        new Set(
          ids
            .map(String)
            .filter((id) => id !== ALL_STOPS_VALUE)
            .filter((id) => optionIds.has(id))
        )
      ),
    [optionIds]
  );

  const globalParentIdsForThisLine = React.useMemo(() => {
    const ids = new Set<string>();

    for (const globalStop of selectedStopsGlobal) {
      const parentId = normalizeParentId(globalStop.id);

      if (optionIds.has(parentId)) {
        ids.add(parentId);
      }
    }

    return Array.from(ids);
  }, [selectedStopsGlobal, optionIds]);

  const visibleSelected = React.useMemo(
    () => cleanStopIds(globalParentIdsForThisLine),
    [globalParentIdsForThisLine, cleanStopIds]
  );

  const visibleSelectedSet = React.useMemo(
    () => new Set(visibleSelected),
    [visibleSelected]
  );

  const allSelected =
    parentStopIds.length > 0 &&
    parentStopIds.every((id) => visibleSelectedSet.has(id));

  const selectValue = React.useMemo(
    () =>
      allSelected ? [ALL_STOPS_VALUE, ...visibleSelected] : visibleSelected,
    [allSelected, visibleSelected]
  );

  const stopOptions = React.useMemo(() => {
    const options = parentStops
      .map((stop) => ({
        value: String(stop.id),
        label: stop.name,
      }))
      .sort((a, b) => SV_COLLATOR.compare(a.label, b.label));

    return [
      {
        value: ALL_STOPS_VALUE,
        label: allSelected ? "Avmarkera alla" : "Välj alla",
      },
      ...options,
    ];
  }, [parentStops, allSelected]);

  const blurActiveElement = React.useCallback(() => {
    requestAnimationFrame(() => {
      (document.activeElement as HTMLElement | null)?.blur();
    });
  }, []);

  const handleChange = React.useCallback(
    (nextRaw: string[]) => {
      const nextUnique = Array.from(new Set(nextRaw.map(String)));

      const hadAllValue = selectValue.includes(ALL_STOPS_VALUE);
      const hasAllValue = nextUnique.includes(ALL_STOPS_VALUE);

      const toggledAllValue = hadAllValue !== hasAllValue;

      if (toggledAllValue) {
        if (allSelected) {
          for (const id of parentStopIds) {
            onRemoveStopGlobal?.(id);
          }
          blurActiveElement();
          return;
        }

        const newlyAdded = parentStopIds.filter(
          (id) => !visibleSelectedSet.has(id)
        );

        for (const id of newlyAdded) {
          const stop = byId.get(id);

          if (stop) {
            onAddStopFromLine(stop);
          }
        }
        blurActiveElement();

        return;
      }

      const next = cleanStopIds(nextUnique);
      const nextSet = new Set(next);

      const newlyAdded = next.filter((id) => !visibleSelectedSet.has(id));
      const removed = visibleSelected.filter((id) => !nextSet.has(id));

      for (const id of newlyAdded) {
        const stop = byId.get(id);

        if (stop) {
          onAddStopFromLine(stop);
        }
      }

      for (const id of removed) {
        onRemoveStopGlobal?.(id);
      }

      if (newlyAdded.length > 0) {
        blurActiveElement();
      }
    },
    [
      selectValue,
      allSelected,
      parentStopIds,
      visibleSelectedSet,
      visibleSelected,
      byId,
      onAddStopFromLine,
      onRemoveStopGlobal,

      blurActiveElement,
      cleanStopIds,
    ]
  );

  const totalStops = parentStops.length;
  const selectedCount = visibleSelected.length;

  const summaryText = selectionSummary(
    Boolean(loading),
    totalStops,
    selectedCount,
    "hållplatser",
    "hållplats"
  );

  const isLoading = loading;
  const stopsLabelId = `line-${lineKey}-stops-label`;

  return (
    <div className="@container">
      <Card className="border-0 pt-6 pb-8 rounded-[var(--box-border-radius)] gap-0 bg-[var(--card-color-bg-interacted)]">
        <CardHeader className=" !flex gap-4 flex-col mb-0">
          <div className="flex flex-row gap-2 !items-top w-full">
            <div className="flex flex-row gap-2 @max-[300px]:!flex-col">
              <LineTitleTrafficBadge line={line} />

              <div className="flex flex-col">
                <span className="mb-0 font-medium font-size-[var(--caption-small-text-size)]">
                  {lineLabel}
                </span>
                <span className="text-sm opacity-70">{line.Heading}</span>
              </div>
            </div>
            <CompactButton
              className="ml-auto min-w-fit h-fit"
              variant="secondary"
              iconName={liveIsActive ? "radio" : "radio-off"}
              showIcon
              size="small"
              iconSize={16}
              activated={liveIsActive}
              aria-pressed={liveIsActive}
              disabled={liveUnavailable}
              onClick={() => onToggleShowLive(lineKey, !showLiveChecked)}
            >
              Live
            </CompactButton>
          </div>
        </CardHeader>

        <CardContent>
          {liveUnavailable && (
            <Alert dismissible variant="warning" className="mt-2 mb-2">
              <AlertTitle>Ingen live-trafik hittades</AlertTitle>
              <AlertDescription className="text-sm mt-2">
                Vi får ingen live-data för den här linjen just nu.
              </AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div
              role="status"
              aria-live="polite"
              className=" flex-col gap-2 flex"
            >
              {" "}
              <Spinner size={16} className="min-h-[36px] items-start mt-4" />
              <div className="h-11 w-full rounded-md bg-[var(--skeleton-color-bg,#e9eef6)] animate-pulse" />
              <span className="sr-only">Laddar hållplatser…</span>
            </div>
          ) : parentStops.length > 0 ? (
            <div className="flex flex-col">
              <Label id={stopsLabelId} className="mt-4 mb-2">
                Hållplatser på linjen
              </Label>
              <MultiSelect
                options={stopOptions}
                value={selectValue}
                onValueChange={handleChange}
                placeholder={summaryText}
                ariaLabelledBy={stopsLabelId}
                showCounter={false}
              />
            </div>
          ) : (
            <Alert variant="warning">
              <AlertTitle>Inga hållplatser hittade.</AlertTitle>
              <AlertDescription>
                Inga hållplatser hittade för denna linje.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LineTitleTrafficBadge({ line }: { line: LineDetails }) {
  const lookupIds = React.useMemo(() => getTrafficMetaLookupIds(line), [line]);

  const trafficMetaFetcher = React.useCallback(
    async (lineIdFromHook: string) => {
      const ids = uniqueStrings([lineIdFromHook, ...lookupIds]);

      let lastResult: { iconCss?: string; groupCss?: string } = {};

      for (const id of ids) {
        try {
          const result = await fetchTrafficMetaViaFind(id);

          lastResult = result;

          if (result.iconCss || result.groupCss) {
            return result;
          }
        } catch {}
      }

      return lastResult;
    },
    [lookupIds]
  );

  const { meta } = useTrafficMeta(line, {
    fetcher: trafficMetaFetcher,
  });

  const badgeText = React.useMemo(() => getLinePublicLabel(line), [line]);

  return (
    <TrafficBadge
      meta={meta}
      className="!min-w-[60px] "
      styleOpts={{ includeCssIconAsFirst: false }}
    >
      {badgeText}
    </TrafficBadge>
  );
}

function shallowEqualStops(a: Stop[], b: Stop[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
  }

  return true;
}
